/**
 * V2 Profile Sheet — Premium glass-morphism bottom drawer
 * matching the Apple TV aesthetic of the V2 app shell.
 */
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCircle, LogOut, ChevronRight, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Drawer,
  DrawerContent,
  DrawerHandle,
} from '@/components/ui/drawer';
import { StableAvatar } from '@/components/ui/stable-avatar';
import { useNativeGestures } from '@/hooks/use-native-gestures';
import { cn } from '@/lib/utils';

interface V2ProfileSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  avatarUrl: string | null;
  displayName: string;
  email: string;
  isKidsProfile: boolean;
  subscriptionStatus?: string | null;
  onSwitchProfiles: () => void;
  onLogout: () => void;
}

interface MenuItem {
  id: string;
  icon: typeof Users;
  label: string;
  desc: string;
  path: string | null;
  accent?: string;
}

const menuItems: MenuItem[] = [
  { id: 'profiles', icon: Users, label: 'Switch Profiles', desc: 'Change reading profile', path: null },
  { id: 'profile', icon: UserCircle, label: 'My Profile', desc: 'Account & preferences', path: '/profile' },
];

export const V2ProfileSheet = memo(function V2ProfileSheet({
  isOpen,
  onOpenChange,
  avatarUrl,
  displayName,
  email,
  isKidsProfile,
  subscriptionStatus,
  onSwitchProfiles,
  onLogout,
}: V2ProfileSheetProps) {
  const navigate = useNavigate();
  const { triggerLight, triggerMedium } = useNativeGestures({ hapticFeedback: true });

  const handleItemClick = useCallback((item: MenuItem) => {
    triggerLight();
    if (item.id === 'profiles') {
      onSwitchProfiles();
    } else if (item.path) {
      navigate(item.path);
      window.scrollTo(0, 0);
    }
    onOpenChange(false);
  }, [triggerLight, onSwitchProfiles, navigate, onOpenChange]);

  const handleLogout = useCallback(() => {
    triggerMedium();
    onLogout();
    onOpenChange(false);
  }, [triggerMedium, onLogout, onOpenChange]);

  const filteredItems = menuItems.filter(item => {
    if ((item.id === 'profile' || item.id === 'settings') && isKidsProfile) return false;
    return true;
  });

  const isActive = subscriptionStatus === 'active';
  const isCancelled = subscriptionStatus === 'cancelled';

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} handleOnly>
      <DrawerContent
        className="border-0 focus:outline-none"
        style={{
          background: 'rgba(20, 20, 22, 0.85)',
          backdropFilter: 'blur(60px) saturate(180%)',
          WebkitBackdropFilter: 'blur(60px) saturate(180%)',
          borderTop: '0.5px solid rgba(255,255,255,0.08)',
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <DrawerHandle className="w-9 h-[3px] rounded-full bg-white/25" />
        </div>

        {/* Profile Header */}
        <motion.div
          className="px-6 pt-3 pb-5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="flex items-center gap-4">
            {/* Avatar with gradient ring */}
            <div className="relative">
              <div
                className="absolute -inset-[2px] rounded-full opacity-60"
                style={{
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.6), rgba(168,85,247,0.6), rgba(236,72,153,0.4))',
                }}
              />
              <StableAvatar
                avatarUrl={avatarUrl}
                fallbackText={displayName}
                size="lg"
                className="relative h-[60px] w-[60px] border-2 border-black/50"
              />
              {/* Online indicator */}
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-[2.5px] border-[#141416]" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[20px] font-semibold text-white tracking-tight truncate leading-tight">
                {displayName}
              </p>
              <p className="text-[13px] text-white/40 truncate mt-0.5">{email}</p>

              {/* Subscription badge */}
              <div className="flex items-center gap-1.5 mt-2">
                {isActive && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide uppercase"
                    style={{
                      background: 'linear-gradient(135deg, rgba(52,211,153,0.15), rgba(16,185,129,0.1))',
                      color: 'rgb(110,231,183)',
                      border: '0.5px solid rgba(52,211,153,0.2)',
                    }}
                  >
                    <Crown size={10} />
                    Premium
                  </span>
                )}
                {isCancelled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide uppercase text-amber-300/80"
                    style={{
                      background: 'rgba(251,191,36,0.1)',
                      border: '0.5px solid rgba(251,191,36,0.15)',
                    }}
                  >
                    Ending Soon
                  </span>
                )}
                {!isActive && !isCancelled && (
                  <span className="text-[11px] font-medium text-white/30 tracking-wide uppercase">
                    Free Plan
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Menu Items */}
        <div className="px-4 pb-2" data-vaul-no-drag>
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '0.5px solid rgba(255,255,255,0.06)',
            }}
          >
            <AnimatePresence>
              {filteredItems.map((item, index) => {
                const Icon = item.icon;
                const isLast = index === filteredItems.length - 1;
                return (
                  <motion.button
                    key={item.id}
                    data-vaul-no-drag
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + index * 0.04, duration: 0.3, ease: 'easeOut' }}
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      'flex items-center gap-3.5 w-full px-4 py-3.5 active:bg-white/[0.06] transition-colors touch-manipulation',
                      !isLast && 'border-b border-white/[0.04]'
                    )}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                      }}
                    >
                      <Icon size={18} className="text-white/70" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-[15px] font-medium text-white/90 leading-tight">{item.label}</p>
                      <p className="text-[12px] text-white/35 mt-0.5">{item.desc}</p>
                    </div>
                    <ChevronRight size={16} className="text-white/20" />
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        </div>

        {/* Logout */}
        <div className="px-4 pt-1 pb-6" data-vaul-no-drag>
          <motion.button
            data-vaul-no-drag
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            onClick={handleLogout}
            className="flex items-center justify-center w-full py-3 rounded-2xl active:bg-red-500/10 transition-colors touch-manipulation"
            style={{
              background: 'rgba(239,68,68,0.06)',
              border: '0.5px solid rgba(239,68,68,0.1)',
            }}
          >
            <LogOut size={16} className="text-red-400/80 mr-2" />
            <span className="text-[15px] font-medium text-red-400/80">Sign Out</span>
          </motion.button>
        </div>

        {/* Safe area spacer */}
        <div style={{ height: 'env(safe-area-inset-bottom)' }} />
      </DrawerContent>
    </Drawer>
  );
});
